import { Component, type ErrorInfo, type ReactNode } from 'react';
import { TriangleAlert as AlertTriangle } from 'lucide-react';
import { Sentry } from '../../lib/sentry';

interface Props {
  children: ReactNode;
  // Changing this clears a caught error, e.g. the route path, so navigating away recovers.
  resetKey?: unknown;
}

interface State {
  error: Error | null;
}

// Contains a render error to the page it happened in, so the navigation and the rest of the app
// stay usable instead of the whole app falling back to the root error screen.
export default class SectionErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, { contexts: { react: { componentStack: info.componentStack } } });
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex flex-col items-center justify-center px-4 py-24 text-center">
        <AlertTriangle className="mb-3 h-6 w-6 text-app-warning" aria-hidden="true" />
        <h1 className="mb-1 text-lg font-semibold text-app-ink">This page failed to load</h1>
        <p className="mb-6 text-sm text-app-muted">The error has been reported. You can try again or use the navigation to keep working.</p>
        <button onClick={() => this.setState({ error: null })} className="btn-primary">
          Try again
        </button>
      </div>
    );
  }
}
